<script lang="ts">
  /**
   * Canonical user reference. Renders the user's avatar + display text and links
   * to /users/{id}. Self-fetches the avatar via the shared module-level cache so
   * many instances of the same user only hit the backend once.
   */
  import { onMount } from "svelte";
  import { link } from "svelte-spa-router";
  import Avatar from "./Avatar.svelte";
  import { getCachedAvatar, peekAvatar } from "./avatarCache";

  /** User id — drives the /users/{id} href and the avatar fetch key. */
  export let id: number | string;
  export let email: string | null = null;
  export let fullName: string | null = null;
  export let displayName: string | null = null;
  /** When false, skip the avatar fetch entirely (no image known). */
  export let hasImage = true;
  export let size = 24;
  /** When false, render a plain inline span instead of a link. */
  export let link_ = true;
  export { link_ as link };

  // Prefer a stable string key for the cache (numeric id, else email).
  $: key = id != null && String(id) !== "" ? String(id) : email ?? null;

  $: text =
    fullName
      ? displayName
        ? `${fullName} (${displayName})`
        : fullName
      : displayName ?? email ?? `#${id}`;

  let img: string | null = key ? peekAvatar(key) ?? null : null;

  onMount(() => {
    if (hasImage === false || !key) return;
    getCachedAvatar(key)
      .then((url) => {
        img = url;
      })
      .catch(() => {
        /* silent — keep initials fallback */
      });
  });
</script>

{#if link_}
  <a
    class="user-link"
    href="/users/{id}"
    use:link
    title={email ?? undefined}
    data-testid="user-link-{id}"
  >
    <Avatar image={img} name={displayName ?? email} {size} />
    <span class="label">{text}</span>
  </a>
{:else}
  <span
    class="user-link"
    title={email ?? undefined}
    data-testid="user-link-{id}"
  >
    <Avatar image={img} name={displayName ?? email} {size} />
    <span class="label">{text}</span>
  </span>
{/if}

<style>
  .user-link {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: inherit;
    text-decoration: none;
    max-width: 100%;
    min-width: 0;
  }
  a.user-link {
    color: #34d399;
  }
  a.user-link:hover .label {
    text-decoration: underline;
  }
  .label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
</style>
